import { useCallback, useRef, useState } from 'react';
import { apiStore } from '@/stores/ApiStore';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { paymentConfirmationStore } from '@/stores/PaymentConfirmationStore';
import { getErrorMessage } from '@/hooks/usePaymentErrors';
import {
  sendPaymentFailed,
  sendPaymentSuccess,
} from '@/hooks/useWebViewMessaging';
import {
  buildAppsFlyerDeeplinkFromPath,
  buildAppsFlyerRedirectUrl,
} from '@/utils/deeplink';
import { consumeAppsFlyerParams } from '@/utils/appsFlyerParams';
import {
  buildAttConversionClickPayload,
  getStoredAttParams,
  setConversionClickSent,
  wasConversionClickSent,
} from '@/utils/attParams';
import { navigateWithPaymentOverlay } from '@/utils/paymentNavigation';
import { isBrowserSessionEnvironment } from '@/utils/sessionEnvironment';
import { ROUTES } from '@/constants/routes';
import type { SubscriptionStatus } from '@/types/api';

type SessionCheckoutSource = 'account' | 'paywall';

async function registerSessionAttributionClick(): Promise<void> {
  const email = apiStore.user?.email?.trim();
  const attParams = getStoredAttParams();
  if (!email || !attParams) {
    return;
  }

  const clickPayload = buildAttConversionClickPayload(
    {
      ...attParams,
      att_email: email,
    },
    email,
  );
  if (!clickPayload || wasConversionClickSent(clickPayload)) {
    return;
  }

  try {
    await apiStore.conversionClick(clickPayload);
    setConversionClickSent(clickPayload);
  }
  catch (error) {
    console.warn('Unable to register attribution before session activation', error);
  }
}

export function useSessionSubscriptionCheckout(source: SessionCheckoutSource) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const processingRef = useRef(false);
  const available = isBrowserSessionEnvironment();

  const activate = useCallback(async (planId: string): Promise<SubscriptionStatus | null> => {
    if (processingRef.current || !planId) {
      return null;
    }

    if (!available) {
      setError('Session checkout is available in a web browser only.');
      return null;
    }

    processingRef.current = true;
    setProcessing(true);
    setError('');
    paymentConfirmationStore.start();

    try {
      await registerSessionAttributionClick();
      const status = await apiStore.activateSessionSubscription({ planId });
      if (!status.hasAccess) {
        throw new Error('The server did not confirm subscription access.');
      }

      subscriptionStore.applyStatus(status);
      sendPaymentSuccess();

      const appsFlyerParams = consumeAppsFlyerParams();
      const handoffResult = await apiStore.createHandoff();
      if (handoffResult.ok && handoffResult.token) {
        const deeplink = buildAppsFlyerDeeplinkFromPath(
          window.location.pathname,
          {
            attParams: source === 'paywall' ? getStoredAttParams() : null,
            handoffToken: handoffResult.token,
            includePaywall: source === 'paywall',
          },
        );
        const fallbackCampaign = source === 'paywall' && deeplink.paywallVersion
          ? `paywall_${deeplink.paywallVersion}`
          : 'account_payment';
        const redirectUrl = buildAppsFlyerRedirectUrl(
          appsFlyerParams,
          deeplink,
          fallbackCampaign,
        );
        navigateWithPaymentOverlay(redirectUrl);
        return status;
      }

      if (source === 'paywall') {
        navigateWithPaymentOverlay(ROUTES.ACCOUNT.SUBSCRIPTION);
      }
      return status;
    }
    catch (activationError) {
      const message = getErrorMessage(activationError);
      setError(message);
      sendPaymentFailed(message);
      return null;
    }
    finally {
      paymentConfirmationStore.stopUnlessNavigating();
      processingRef.current = false;
      setProcessing(false);
    }
  }, [available, source]);

  const clearError = useCallback(() => {
    setError('');
  }, []);

  return {
    activate,
    available,
    clearError,
    error,
    processing,
  };
}
